import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument, type TileRef } from "../document/types.ts";
import { baseLayerId } from "./base-layer.ts";
import { pickAt, type PickHit, type PickOptions } from "./pick.ts";
import type { WorldRect } from "./many-props.ts";
import type { FrameSizeOf, OpaqueAt } from "./prop-hit.ts";

/**
 * What the select tool's two buttons would take under the pointer (ADR-128 decisions 41, 41a, 41b and 59), worked
 * out in one place so the outline before a press, the words of the mouse help and the press itself read the same
 * answer and cannot disagree.
 *
 * The left button's target is the select tool's own pick (`pickAt`), with the options the press runs with; while
 * a prop or a marker layer is active it takes no ground, tile or terrain, since a press there selects no tile and
 * makes no layer active (decision 57). The
 * right button removes one thing: the topmost marker, prop or painted tile cell under the pointer on any usable
 * layer, by that same pick. It never takes the map's base, whether or not the base is locked, and it never takes
 * terrain: where terrain is the topmost ground under the pointer nothing goes, not the terrain and not a tile it
 * covers, which the builder cannot see. With a zone or a door layer active, whose overlay is then on screen, that
 * layer's painted quarter cell under the pointer goes first; over an unpainted one the rule above applies.
 * Collision mode is not read here: the select tool keeps its own rule for it.
 */

/**
 * What a right press would remove: a prop, a marker, a painted tile cell, the active zone or door layer's painted
 * quarter cell by its index in that layer's grid, or nothing, because terrain is the topmost ground (`terrain`,
 * naming its layer) or because nothing a right press takes lies there (`bare`).
 */
export type EraseTarget =
  | { readonly kind: "prop"; readonly layerId: string; readonly propId: string }
  | { readonly kind: "entity"; readonly layerId: string; readonly entityId: string }
  | { readonly kind: "tile"; readonly layerId: string; readonly x: number; readonly y: number }
  | { readonly kind: "zone"; readonly layerId: string; readonly index: number }
  | { readonly kind: "door"; readonly layerId: string; readonly index: number }
  | { readonly kind: "none"; readonly reason: "terrain"; readonly layerId: string }
  | { readonly kind: "none"; readonly reason: "bare" };

/**
 * What each button would take: the left button's pick, null over nothing, and the right button's target.
 */
export interface PressTargets {
  readonly select: PickHit | null;
  readonly erase: EraseTarget;
}

/**
 * What the targets are named by: a frame's name, a marker type's label and a zone type's label.
 */
export interface PressNames {
  readonly frameName: (frame: TileRef) => string;
  readonly entityLabel: (type: string) => string;
  readonly zoneLabel: (type: string) => string;
}

const BARE: EraseTarget = { kind: "none", reason: "bare" };

/**
 * The active zone or door layer's painted quarter cell under a point.
 *
 * @param map The map.
 * @param layer The active layer.
 * @param tileSize Tile size in pixels.
 * @param wx World pixel x.
 * @param wy World pixel y.
 * @returns The target, or null when the layer is of another kind, the point is off the map or the cell is unpainted.
 */
function quarterCell(map: MapDocument, layer: LayerDocument | undefined, tileSize: number, wx: number, wy: number): EraseTarget | null {
  if (layer === undefined || (layer.kind !== "zone" && layer.kind !== "door")) {
    return null;
  }
  const unit = tileSize / COLLISION_CELLS_PER_TILE;
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const x = Math.floor(wx / unit);
  const y = Math.floor(wy / unit);
  if (x < 0 || y < 0 || x >= width || y >= map.height * COLLISION_CELLS_PER_TILE) {
    return null;
  }
  const index = y * width + x;
  return (layer.cells[index] ?? 0) === 0 ? null : { kind: layer.kind, layerId: layer.id, index };
}

/**
 * Works out what a left and a right press of the select tool would take at a world point.
 *
 * @param map The map.
 * @param usable Whether a layer may be picked from (visible and unlocked).
 * @param sizeOf Frame size lookup for props.
 * @param opaque Frame pixel opacity lookup for props.
 * @param tileSize Tile size in pixels.
 * @param wx World pixel x.
 * @param wy World pixel y.
 * @param press The pick options of the press, the pick-through modifier's among them, and the active layer's id.
 * @returns The two targets.
 */
export function pressTargets(map: MapDocument, usable: (layerId: string) => boolean, sizeOf: FrameSizeOf, opaque: OpaqueAt, tileSize: number, wx: number, wy: number, press: { readonly options: PickOptions; readonly activeId: string | null }): PressTargets {
  const picked = pickAt(map, usable, sizeOf, opaque, tileSize, wx, wy, press.options);
  const mode = press.activeId === null ? undefined : map.layers.find((layer) => layer.id === press.activeId)?.kind;
  const select = picked !== null && (picked.kind === "tile" || picked.kind === "terrain") && (mode === "prop" || mode === "entity") ? null : picked;
  const active = press.activeId === null || !usable(press.activeId) ? undefined : map.layers.find((layer) => layer.id === press.activeId);
  const cell = quarterCell(map, active, tileSize, wx, wy);
  if (cell !== null) {
    return { select, erase: cell };
  }
  const base = baseLayerId(map);
  const hit = base !== null && usable(base) ? pickAt(map, (id) => id !== base && usable(id), sizeOf, opaque, tileSize, wx, wy, press.options) : picked;
  if (hit === null) {
    return { select, erase: BARE };
  }
  return { select, erase: hit.kind === "terrain" ? { kind: "none", reason: "terrain", layerId: hit.layerId } : hit };
}

/**
 * A layer's name.
 *
 * @param map The map.
 * @param layerId The layer.
 * @returns The name, or the id for a layer the map does not hold.
 */
function layerName(map: MapDocument, layerId: string): string {
  return map.layers.find((layer) => layer.id === layerId)?.name ?? layerId;
}

/**
 * What a prop, a marker or a tile cell is called with its layer: "oak tree on Props", "Tile 12 on Paths".
 *
 * @param map The map.
 * @param hit The thing.
 * @param names The lookups.
 * @returns The words, or null for a thing the map no longer holds.
 */
function thingWords(map: MapDocument, hit: Extract<PickHit, { kind: "prop" | "entity" | "tile" }>, names: PressNames): string | null {
  const layer = map.layers.find((candidate) => candidate.id === hit.layerId);
  if (hit.kind === "prop") {
    const prop = layer?.kind === "prop" ? layer.props.find((candidate) => candidate.id === hit.propId) : undefined;
    return prop === undefined ? null : `${names.frameName(prop.frame)} on ${layer?.name ?? hit.layerId}`;
  }
  if (hit.kind === "entity") {
    const entity = layer?.kind === "entity" ? layer.entities.find((candidate) => candidate.id === hit.entityId) : undefined;
    return entity === undefined ? null : `${names.entityLabel(entity.type)} on ${layer?.name ?? hit.layerId}`;
  }
  const packed = layer?.kind === "tile" ? (layer.tiles[hit.y * map.width + hit.x] ?? EMPTY_TILE) : EMPTY_TILE;
  return packed === EMPTY_TILE ? null : `Tile ${unpackTile(packed).tile} on ${layer?.name ?? hit.layerId}`;
}

/**
 * What the mouse help says beside each button (ADR-128 decisions 41a and 59): the left button's target after
 * "select:", the right button's after "erase:", each with its layer, and "nothing" where there is none, with the
 * reason when terrain is what stops the erase.
 *
 * @param map The map.
 * @param targets The targets, as `pressTargets` gave them.
 * @param names The lookups.
 * @returns "select: oak tree on Props" and "erase: oak tree on Props"; over terrain "select: Stone floor terrain"
 *   and "erase: nothing; Stone floor is terrain"; a zone cell "erase: Sanctuary cell on Willowmere Village".
 */
export function pressWords(map: MapDocument, targets: PressTargets, names: PressNames): { readonly select: string; readonly erase: string } {
  const { select, erase } = targets;
  const selected = select === null ? null : select.kind === "terrain" ? `${layerName(map, select.layerId)} terrain` : thingWords(map, select, names);
  let erased: string | null;
  if (erase.kind === "none") {
    erased = erase.reason === "terrain" ? `nothing; ${layerName(map, erase.layerId)} is terrain` : null;
  } else if (erase.kind === "zone" || erase.kind === "door") {
    const layer = map.layers.find((candidate) => candidate.id === erase.layerId);
    const value = layer?.kind === "zone" || layer?.kind === "door" ? (layer.cells[erase.index] ?? 0) : 0;
    const what = layer?.kind === "zone" ? names.zoneLabel(layer.types[value - 1] ?? "") : layer?.kind === "door" ? (layer.doors[value - 1]?.name ?? "Door") : "";
    erased = `${what} cell on ${layerName(map, erase.layerId)}`;
  } else {
    erased = thingWords(map, erase, names);
  }
  return { select: `select: ${selected ?? "nothing"}`, erase: `erase: ${erased ?? "nothing"}` };
}

/**
 * What the status bar says when a right press removes nothing (ADR-128 decision 41).
 *
 * @param map The map.
 * @param erase The right button's target.
 * @returns The sentence, or null when the press removes something.
 */
export function eraseRefusal(map: MapDocument, erase: EraseTarget): string | null {
  if (erase.kind !== "none") {
    return null;
  }
  return erase.reason === "terrain" ? `${layerName(map, erase.layerId)} is terrain: erase it with the terrain brush and Shift, or a box erase with All layers` : "Nothing here to erase";
}

/**
 * The box of one target in world pixels: a prop by the cells its art covers, as the caller sizes it; a marker by
 * the tile round its point; a tile or terrain cell by its tile; a zone or door cell by its quarter cell.
 *
 * @param map The map.
 * @param target The target.
 * @param tileSize Tile size in pixels.
 * @param propBox The box a placed prop's art covers, null when the map holds no such prop.
 * @returns The box, or null for no target or a thing the map no longer holds.
 */
function boxOf(map: MapDocument, target: PickHit | EraseTarget, tileSize: number, propBox: (layerId: string, propId: string) => { readonly x: number; readonly y: number; readonly w: number; readonly h: number } | null): WorldRect | null {
  switch (target.kind) {
    case "none":
      return null;
    case "prop": {
      const box = propBox(target.layerId, target.propId);
      return box === null ? null : { left: box.x, top: box.y, right: box.x + box.w, bottom: box.y + box.h };
    }
    case "entity": {
      const layer = map.layers.find((candidate) => candidate.id === target.layerId);
      const entity = layer?.kind === "entity" ? layer.entities.find((candidate) => candidate.id === target.entityId) : undefined;
      if (entity === undefined) {
        return null;
      }
      const left = (entity.x - 0.5) * tileSize;
      const top = (entity.y - 0.5) * tileSize;
      return { left, top, right: left + tileSize, bottom: top + tileSize };
    }
    case "tile":
    case "terrain":
      return { left: target.x * tileSize, top: target.y * tileSize, right: (target.x + 1) * tileSize, bottom: (target.y + 1) * tileSize };
    case "zone":
    case "door": {
      const unit = tileSize / COLLISION_CELLS_PER_TILE;
      const width = map.width * COLLISION_CELLS_PER_TILE;
      const x = target.index % width;
      const y = Math.floor(target.index / width);
      return { left: x * unit, top: y * unit, right: (x + 1) * unit, bottom: (y + 1) * unit };
    }
  }
}

/**
 * The outlines drawn under the pointer before a press (ADR-128 decisions 41a and 59): the left button's target,
 * and the right button's own where it is another thing. Where both buttons take the same thing one outline says
 * so, and where the right button takes nothing it has none. The right click is never blind: where the left
 * button's target is not shown at all, the right button's is outlined on its own.
 *
 * @param map The map.
 * @param targets The targets, as `pressTargets` gave them.
 * @param tileSize Tile size in pixels.
 * @param propBox The box a placed prop's art covers, null when the map holds no such prop.
 * @param only `erase` where the left button's target is not shown (the All layers reach, whose left press
 *   draws an area): the right button's target is then outlined whatever the left button would take.
 * @returns The two boxes in world pixels, each null when there is nothing to outline.
 */
export function pressBoxes(map: MapDocument, targets: PressTargets, tileSize: number, propBox: (layerId: string, propId: string) => { readonly x: number; readonly y: number; readonly w: number; readonly h: number } | null, only?: "erase"): { readonly select: WorldRect | null; readonly erase: WorldRect | null } {
  const { select, erase } = targets;
  if (only === "erase") {
    return { select: null, erase: boxOf(map, erase, tileSize, propBox) };
  }
  const same = select !== null && erase.kind === select.kind && erase.layerId === select.layerId && JSON.stringify(erase) === JSON.stringify(select);
  return { select: select === null ? null : boxOf(map, select, tileSize, propBox), erase: same ? null : boxOf(map, erase, tileSize, propBox) };
}
